import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Image, TextInput, TouchableOpacity, StyleSheet, ScrollView, Linking } from 'react-native';
import { FaGlobe, FaInstagram, FaFacebook, FaTwitter, FaYoutube, FaTripadvisor, FaTelegram, FaWhatsapp, FaPinterest, FaLinkedin, FaReddit, FaWeixin, FaLine, FaWeibo, FaStar } from 'react-icons/fa';
import { SiTiktok, SiWechat, SiLine, SiBilibili, SiXiaohongshu } from 'react-icons/si';
import PlatformIcon from '../common/PlatformIcon';
import InlineEditRow from './shared/InlineEditRow';
import BookingPlatformsRow from './shared/BookingPlatformsRow';
import CoverImage from './shared/CoverImage';
/**
 * RestaurantEditorView
 *
 * Unified editor implementation currently used by both Restaurant and Lodging flows.
 *
 * External usage guidance:
 * - Restaurant flows: import { RestaurantEditorView } from '@cubemeet/ui'
 * - Lodging flows:    import { LodgingEditorView } from '@cubemeet/ui'
 *
 * Why this design?
 * - We keep a stable, business-named API for callers while reusing implementation internally today.
 * - LodgingEditorView wraps this component with displayVariant="lodging" to prevent misconfiguration.
 * - If lodging diverges in the future, we can swap LodgingEditorView to a dedicated component without breaking imports.
 */

export interface RestaurantEditorViewProps {
  mode?: 'view' | 'edit';
  data?: any; // payload with fields like title, cover_image, address, opening_hours, phone, cuisine, signatureDishes, duration_minutes, price_level
  onChange?: (patch: any) => void;
  onSave?: () => void;
  onCancel?: () => void;
  // when true the parent (modal) renders the full-width header image and this component should not render its own cover
  hideCover?: boolean;
  // when true the view should behave as strict inline editor: empty values show a single underline with placeholder text,
  // clicking a line switches to a single-line input and blur/enter auto-saves via onChange
  editableInline?: boolean;
  // optional supabase client for querying affiliated booking platforms
  supabaseClient?: any;
  // card ID for fetching associated platform products
  cardId?: string;
  // render variant: when 'lodging', hide cuisine/restaurant-only fields and adjust labels
  displayVariant?: 'restaurant' | 'lodging';
  // optional explicit platform_product_id to fetch booking product
  platformProductId?: string;
  // optional explicit list of platform product ids to fetch multiple booking options
  platformProductIds?: string[];
}

const RestaurantEditorView: React.FC<RestaurantEditorViewProps> = ({ mode = 'view', data = {}, onChange, onSave, onCancel, hideCover = false, editableInline = false, supabaseClient, cardId, displayVariant = 'restaurant', platformProductId, platformProductIds }) => {
  
  // --- CRITICAL: Helper to update metaPairs when editing inline fields ---
  // This ensures we maintain a SINGLE DATA SOURCE (metaPairs) for all structured fields.
  // When a user edits a field (e.g., bookingTips), we update the corresponding metaPairs item.
  const updateMetaPairsField = (fieldKey: string, newValue: any) => {
    if (!onChange) return;
    
    // Mapping from field keys to metaPairs labels
    const keyToLabelMap: { [key: string]: string } = {
      'duration_minutes': '建议时长',
      'price_range': '参考价位',
      'phone': '电话',
      'website': '网站',
      'restaurantName': '餐厅',
      'hotelName': '酒店',
      'cuisine': '菜系',
      'signatureDishes': '推荐菜',
      'bookingTips': '预订建议',
      'starRating': '星级',
      'amenities': '设施',
      'checkInTime': '入住时间',
      'checkOutTime': '退房时间',
      'coordinates': '坐标',
    };
    
    const label = keyToLabelMap[fieldKey];
    if (!label) {
      // If no mapping, just update the flat field (for backwards compatibility)
      const patch: any = {};
      patch[fieldKey] = newValue;
      onChange(patch);
      return;
    }
    
    // Get current metaPairs (from data.metaPairs or empty array)
    const currentMetaPairs = (data && Array.isArray(data.metaPairs)) ? data.metaPairs.slice() : [];
    
    // Find existing item or create new one
    const existingIndex = currentMetaPairs.findIndex((p: any) => p.label === label);
    
    if (newValue === undefined || newValue === null || newValue === '') {
      // Remove the item if value is empty
      if (existingIndex !== -1) {
        currentMetaPairs.splice(existingIndex, 1);
      }
    } else {
      // Format value for display
      let formattedValue = newValue;
      if (fieldKey === 'duration_minutes' && typeof newValue === 'number') {
        const hours = Math.floor(newValue / 60);
        const minutes = newValue % 60;
        if (hours > 0 && minutes > 0) {
          formattedValue = `${hours} 小时 ${minutes} 分钟`;
        } else if (hours > 0) {
          formattedValue = `${hours} 小时`;
        } else {
          formattedValue = `${minutes} 分钟`;
        }
      }
      
      if (existingIndex !== -1) {
        // Update existing item
        currentMetaPairs[existingIndex] = { label, value: formattedValue };
      } else {
        // Add new item
        currentMetaPairs.push({ label, value: formattedValue });
      }
    }
    
    // Call onChange with updated metaPairs
    onChange({ metaPairs: currentMetaPairs });
  };
  
  // --- START OF MODIFICATION ---
  // NEW LOGIC: Normalize data from `metaPairs` if it exists, to support the new custom_payload structure.
  // This converts the array of {label, value} into a flat object that the rest of the component understands.
  const normalizedDataFromMetaPairs: { [key: string]: any } = {};
  if (data && Array.isArray(data.metaPairs)) {
    console.log('[RestaurantEditorView] Received metaPairs:', data.metaPairs);
    const labelToKeyMap: { [key: string]: string } = {
      '建议时长': 'duration_minutes',
      '参考价位': 'price_range',
      '电话': 'phone',
      '网站': 'website',
      '餐厅': 'restaurantName', // or title
      '酒店': 'hotelName', // or title
      '菜系': 'cuisine',
      '推荐菜': 'signatureDishes',
      '预订建议': 'bookingTips',
      '星级': 'starRating',
      '设施': 'amenities',
      '入住时间': 'checkInTime',
      '退房时间': 'checkOutTime',
      '坐标': 'coordinates',
    };
    
    // Helper: check if a value is a placeholder that should be skipped
    const isPlaceholder = (val: any): boolean => {
      if (val === null || val === undefined || val === '') return true;
      const str = String(val).trim();
      const placeholders = ['待查詢', '待查询', '待查詢測試', '待填写', '待填寫', '—', 'N/A', 'n/a'];
      return placeholders.some(p => str === p || str.toLowerCase() === p.toLowerCase());
    };

    data.metaPairs.forEach((pair: { label: string; value: any }) => {
      const key = labelToKeyMap[pair.label];
      if (key && !isPlaceholder(pair.value)) {
        // Special handling for duration, it should be just the number
        if (key === 'duration_minutes' && typeof pair.value === 'string') {
          const match = pair.value.match(/\d+/);
          normalizedDataFromMetaPairs[key] = match ? parseInt(match[0], 10) : pair.value;
        } else {
          normalizedDataFromMetaPairs[key] = pair.value;
        }
      }
    });
    console.log('[RestaurantEditorView] Normalized data:', normalizedDataFromMetaPairs);
  }

  // Merge the original data with the normalized data, giving priority to the new structure.
  const base = { ...data, ...normalizedDataFromMetaPairs };
  console.log('[RestaurantEditorView] Final base object:', base);
  // --- END OF MODIFICATION ---
  
  // Normalize commonly used fields for rendering
  const cover = base.cover_image || base.coverImage || base.image_url || base.imageUrl || base.image;
  const [coverPreview, setCoverPreview] = useState<string | undefined>(cover);
  useEffect(() => { setCoverPreview(cover); }, [cover]);
  const title = base.title || base.restaurantName || base.name || '';
  const address = typeof base.address === 'string'
    ? base.address
    : (base.location && typeof base.location === 'object' ? formatLocation(base.location) : (base.location || ''));
  const [bookingPlatforms, setBookingPlatforms] = useState<any[]>([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // resolve platform_product_id from props or payload
  const resolvedPlatformProductId = platformProductId || base.platform_product_id || base.platformProductId || base.platformProductID || undefined;
  const resolvedPlatformProductIds: string[] | undefined = (platformProductIds || base.platform_product_ids || base.platformProductIds);

  // helper: logo mapping for known sources (prefer product.display_config.logo_url if present)
  const logoForSource = (source?: string, display_config?: any) => {
    if (display_config && display_config.logo_url) return { uri: display_config.logo_url } as any;
    const s = (source || '').toLowerCase();
    const bySource: Record<string, string> = {
      kkday: '/assets/partners/kkday.png',
      klook: '/assets/partners/klook.png',
      trip: '/assets/partners/trip.png',
      booking: '/assets/partners/booking.png',
      agoda: '/assets/partners/agoda.png',
    };
    const path = bySource[s];
    return path ? { uri: path } : null;
  };

  const deriveSource = (product: any): string | undefined => {
    const direct = (product && (product.source || product.platform || product.vendor)) as string | undefined;
    if (direct) return String(direct).toLowerCase();
    const ec = product?.embed_config;
    const fromEmbed = (ec && (ec.platform || ec.source)) as string | undefined;
    if (fromEmbed) return String(fromEmbed).toLowerCase();
    const dc = product?.display_config;
    const fromDisplay = (dc && (dc.platform || dc.source)) as string | undefined;
    if (fromDisplay) return String(fromDisplay).toLowerCase();
    const link: string | undefined = product?.link || product?.target_url;
    if (link) {
      try {
        const u = new URL(link);
        const h = u.hostname.replace(/^www\./, '').toLowerCase();
        if (h.includes('kkday')) return 'kkday';
        if (h.includes('klook')) return 'klook';
        if (h.includes('booking')) return 'booking';
        if (h.includes('agoda')) return 'agoda';
        if (h.includes('trip.com') || h.includes('ctrip')) return 'trip';
      } catch (e) {}
    }
    return undefined;
  };

  // helper: expand a product into multiple button items when embed_config contains an array/items
  const linkFrom = (obj: any): string | undefined => {
    if (!obj) return undefined;
    return obj.link || obj.url || obj.href || obj.target_url || obj?.attributes?.href;
  };

  const expandProduct = (product: any): any[] => {
    if (!product) return [];
    const ec = product.embed_config;
    const items = Array.isArray(ec) ? ec : (ec && Array.isArray(ec.items) ? ec.items : null);
    if (items && items.length) {
      return items.map((it: any, idx: number) => ({
        ...product,
        id: `${product.id}#${idx}`,
        source: it.platform || it.source || product.source || deriveSource(product),
        link: linkFrom(it) || linkFrom(product),
        display_config: it.display_config || product.display_config,
        analytics: it.analytics || product.analytics,
        embed_config: it.embed_config || it,
      }));
    }
    return [{ ...product, source: product.source || deriveSource(product), link: linkFrom(product) }];
  };

  // Load booking platforms from platform_products table
  useEffect(() => {
    console.log('[RestaurantEditorView] BookingRow useEffect:', {
      cardId,
      hasSupabase: !!supabaseClient,
      mode,
      shouldLoad: !!((cardId || resolvedPlatformProductId || (resolvedPlatformProductIds && resolvedPlatformProductIds.length)) && supabaseClient && mode === 'view'),
      resolvedPlatformProductId,
      resolvedPlatformProductIds,
      displayVariant,
    });
    
    if (!supabaseClient || mode !== 'view') return;
    
    const loadPlatforms = async () => {
      try {
        setLoadingPlatforms(true);
        // If a list of ids is specified, fetch all of them
        if (resolvedPlatformProductIds && resolvedPlatformProductIds.length > 0) {
          const { data: products, error: pErr } = await supabaseClient
            .from('platform_products')
            .select('*')
            .in('id', resolvedPlatformProductIds);
          if (pErr) {
            const code = (pErr && (pErr.code || pErr.status || pErr.name)) as any;
            if (code === 'PGRST116' || code === 406) {
              console.debug('[RestaurantEditorView] by-ids returned none (ok):', pErr);
            } else {
              console.warn('[RestaurantEditorView] Failed to load platform products by ids:', pErr);
            }
          } else {
            const now = new Date();
            const activeExpanded = (products || [])
              .filter(p => p.status === 'active' && (!p.valid_until || new Date(p.valid_until) > now))
              .flatMap(expandProduct);
            setBookingPlatforms(activeExpanded);
            return;
          }
        }

        // If platform_product_id is specified, fetch that single product first
        if (resolvedPlatformProductId && (!resolvedPlatformProductIds || resolvedPlatformProductIds.length === 0)) {
          const { data: product, error: pErr } = await supabaseClient
            .from('platform_products')
            .select('*')
            .eq('id', resolvedPlatformProductId)
            .single();
          if (pErr) {
            const code = (pErr && (pErr.code || pErr.status || pErr.name)) as any;
            if (code === 'PGRST116' || code === 406) {
              console.debug('[RestaurantEditorView] by-id returned none (ok):', pErr);
            } else {
              console.warn('[RestaurantEditorView] Failed to load platform product by id:', pErr);
            }
          } else if (product) {
            // ensure active and not expired
            const active = product.status === 'active' && (!product.valid_until || new Date(product.valid_until) > new Date());
            if (active) {
              // Expand in case of embed_config array
              setBookingPlatforms(expandProduct(product));
            }
          }
        }

        // Do NOT fallback to loading the entire platform_products table.
        // Behaviour: if explicit ids were provided above, we've already attempted to load them and returned.
        // If we reach here it means either no explicit ids were provided or the explicit fetch failed —
        // in either case we should NOT perform a global `select('*')` query that returns unrelated platforms.
        // Instead, show empty bookingPlatforms.
        setBookingPlatforms([]);
        return;
      } catch (e) {
        console.error('[RestaurantEditorView] Error loading booking platforms:', e);
      } finally {
        setLoadingPlatforms(false);
      }
    };

    loadPlatforms();
  }, [cardId, supabaseClient, mode, resolvedPlatformProductId, displayVariant]);

  // Helper to resolve current user id (for audit/tracking in worker)
  const resolveUserId = async (): Promise<string> => {
    try {
      if (supabaseClient?.auth?.getUser) {
        const { data } = await supabaseClient.auth.getUser();
        if (data?.user?.id) return data.user.id;
      }
    } catch (e) {}
    return (base.owner_id || base.user_id || 'anonymous');
  };

  const CARD_IMAGE_UPLOAD_ENDPOINT = (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 'https://card-image-upload.cubemeet.app';

  // display variant helpers must be available before handlers use them
  const isLodging = displayVariant === 'lodging';

  const handleSelectCoverFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleCoverFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setCoverUploading(true);
      const userId = await resolveUserId();
      const form = new FormData();
      form.append('file', file);
      form.append('userId', String(userId));
      if (cardId) form.append('cardId', cardId);
      // Provide cardType so the worker can place uploads under cards/{cardType}/... when no cardId
      form.append('cardType', isLodging ? 'lodging' : 'restaurant');
      form.append('folder', 'cover');

      const resp = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, { method: 'POST', body: form, mode: 'cors' as RequestMode });
      let json: any = null;
      try { json = await resp.json(); } catch (e) {}
      if (!resp.ok) throw new Error((json && (json.error || json.message)) || 'Upload failed');
      // Be tolerant to different worker response shapes
      const resolvePublicUrl = (payload: any): string | undefined => {
        const primary = payload?.data?.url || payload?.url || payload?.publicUrl || payload?.data?.publicUrl || payload?.Location || payload?.data?.Location;
        const key = payload?.data?.key || payload?.key;
        let u = primary as string | undefined;
        const join = (base: string, path: string) => `${base.replace(/\/$/, '')}/${path.replace(/^\/+/, '')}`;
        if (!u && key) {
          const base = (typeof window !== 'undefined' && ((window as any).__CARD_IMAGE_PUBLIC_BASE__ || (window as any).__R2_PUBLIC_URL__)) || payload?.data?.publicBase || payload?.publicBase || 'https://card-images.cubemeet.app';
          if (base) u = join(String(base), String(key));
          else u = String(key); // fallback as-is
        }
        if (u && !/^https?:\/\//i.test(u)) {
          const base = (typeof window !== 'undefined' && ((window as any).__CARD_IMAGE_PUBLIC_BASE__ || (window as any).__R2_PUBLIC_URL__)) || payload?.data?.publicBase || payload?.publicBase || 'https://card-images.cubemeet.app';
          if (base) u = join(String(base), String(u));
        }
        return u;
      };
      const url = resolvePublicUrl(json);
      console.log('[RestaurantEditorView] cover uploaded -> url:', url, 'raw:', json);
      const bust = (u: string) => {
        try { const sep = u.includes('?') ? '&' : '?'; return `${u}${sep}v=${Date.now()}`; } catch { return u; }
      };
      if (url) {
        setCoverPreview(bust(url));
        if (onChange) onChange({ cover_image: url });
      } else {
        console.warn('[RestaurantEditorView] Upload succeeded but no URL found in response:', json);
        if (typeof alert !== 'undefined') alert('上传成功，但未返回图片地址');
      }
    } catch (err) {
      console.warn('[RestaurantEditorView] Cover upload failed:', err);
      if (typeof alert !== 'undefined') alert('封面上传失败，请重试');
    } finally {
      setCoverUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '' as any;
    }
  };

  // Inline editable row component
  const placeholderMap: Record<string, string> = {
    '建议时长': '例如 1 小時 30 分鐘',
    '参考价位': '待查询',
    '电话': '添加电话',
    '网站': '待查询网站',
    '餐厅': '未命名',
    '酒店': '未命名',
    '菜系': '例如 日本料理',
    '推荐菜': '待查询',
    '预订建议': '例如 建议预订',
    '星级': '例如 5',
    '设施': '例如 游泳池, 健身房',
    '入住时间': '例如 15:00',
    '退房时间': '例如 11:00'
  };

  // Shared normalizers and helpers used by InlineEditRow
  const parseDurationToMinutes = (input: string): number | undefined => {
    if (!input) return undefined;
    const s = String(input).trim();
    if (/^\d{1,5}$/.test(s)) return Number(s);
    const colon = s.match(/^(\d{1,2}):(\d{1,2})$/);
    if (colon) {
      const h = Number(colon[1]);
      const m = Number(colon[2]);
      if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
    }
    const unit = s.replace(/\s+/g, '')
      .replace(/小時|小时|時|时/g, 'h')
      .replace(/分鐘|分钟|分/g, 'm')
      .toLowerCase();
    const hm = unit.match(/^(\d+)h(?:(\d+)m)?$/);
    if (hm) {
      const h = Number(hm[1]);
      const m = Number(hm[2] || '0');
      if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
    }
    const onlyM = unit.match(/^(\d+)m(in)?$/);
    if (onlyM) return Number(onlyM[1]);
    const num = s.match(/\d+/g);
    if (num && num.length) return Number(num[0]);
    return undefined;
  };

  const normalizeWebsite = (url: string): string | undefined => {
    let v = (url || '').trim();
    if (!v) return undefined;
    if (/^javascript:/i.test(v)) return undefined;
    if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
    try { new URL(v); } catch { return undefined; }
    return v.replace(/^http:\/\//i, 'https://');
  };

  const normalizePrice = (input: string): string | undefined => {
    let s = (input || '').trim();
    if (!s) return undefined;
    s = s.replace(/\s+/g, '');
    if (/^\d+(?:[.,]\d{1,2})?$/.test(s)) return s;
    const ok = /^(?:NT\$|HK\$|US\$|SG\$|\$|¥|￥|€|£|CNY|TWD|HKD|USD|SGD)?\d+(?:[.,]\d{1,2})?(?:CNY|TWD|HKD|USD|SGD)?$/i;
    if (ok.test(s)) return s;
    const num = s.match(/\d+(?:[.,]\d{1,2})?/);
    const cur = s.match(/NT\$|HK\$|US\$|SG\$|\$|¥|￥|€|£|CNY|TWD|HKD|USD|SGD/i);
    if (num) return (cur ? cur[0] : '') + num[0];
    return undefined;
  };

  const pickSocialIcon = (url: string) => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '').toLowerCase();
      const match = (substr: string) => host.includes(substr);
      if (match('instagram.com')) return { Icon: FaInstagram, color: '#E4405F', label: 'Instagram' };
      if (match('facebook.com')) return { Icon: FaFacebook, color: '#1877F2', label: 'Facebook' };
      if (match('twitter.com') || match('x.com')) return { Icon: FaTwitter, color: '#1DA1F2', label: 'X/Twitter' };
      if (match('youtube.com') || match('youtu.be')) return { Icon: FaYoutube, color: '#FF0000', label: 'YouTube' };
      if (match('tripadvisor.')) return { Icon: FaTripadvisor, color: '#34E0A1', label: 'Tripadvisor' };
      if (match('t.me')) return { Icon: FaTelegram, color: '#229ED9', label: 'Telegram' };
      if (match('whatsapp.com') || match('wa.me')) return { Icon: FaWhatsapp, color: '#25D366', label: 'WhatsApp' };
      if (match('pinterest.')) return { Icon: FaPinterest, color: '#E60023', label: 'Pinterest' };
      if (match('linkedin.')) return { Icon: FaLinkedin, color: '#0A66C2', label: 'LinkedIn' };
      if (match('reddit.')) return { Icon: FaReddit, color: '#FF4500', label: 'Reddit' };
      if (match('tiktok.com') || match('douyin.com') || match('iesdouyin.com')) return { Icon: SiTiktok, color: '#000000', label: 'TikTok' };
      if (match('weixin.qq.com') || match('wechat.com') || match('mp.weixin.qq.com')) return { Icon: SiWechat || FaWeixin, color: '#07C160', label: 'WeChat' };
      if (match('line.me') || match('line.com')) return { Icon: SiLine || FaLine, color: '#06C755', label: 'LINE' };
      if (match('xiaohongshu.com') || match('xhslink.com')) return { Icon: SiXiaohongshu, color: '#FF2442', label: 'RED' };
      if (match('bilibili.com') || match('b23.tv')) return { Icon: SiBilibili, color: '#00A1D6', label: 'bilibili' };
      if (match('weibo.com')) return { Icon: FaWeibo, color: '#E6162D', label: 'Weibo' };
    } catch (e) {}
    return { Icon: FaGlobe, color: '#0B61D6', label: 'Website' };
  };

  // Booking row moved to shared component BookingPlatformsRow

  // moved earlier

  return (
    <ScrollView contentContainerStyle={styles.container}>
      {!hideCover ? (
        <CoverImage uri={coverPreview}>
          {mode === 'edit' ? (
            <>
              <TouchableOpacity style={styles.smallBtn} onPress={handleSelectCoverFile} activeOpacity={0.85} disabled={coverUploading}>
                <Text style={styles.smallBtnText}>{coverUploading ? '上传中…' : '上传封面'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.smallBtn, { backgroundColor: '#111827' }]} onPress={() => { const url = prompt('粘贴图片外链'); if (url) { const sep = url.includes('?') ? '&' : '?'; setCoverPreview(`${url}${sep}v=${Date.now()}`); if (onChange) onChange({ cover_image: url }); } }} activeOpacity={0.85}>
                <Text style={styles.smallBtnText}>使用外链</Text>
              </TouchableOpacity>
              {/* Hidden file input for web */}
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverFileChange} />
            </>
          ) : null}
        </CoverImage>
      ) : null}

      <View style={styles.contentRow}>
        <View style={styles.leftCol}>
          {mode === 'edit' ? (
            <Text style={styles.labelTitle}>{isLodging ? '酒店名称' : '餐厅名称'}</Text>
          ) : null}
          {mode === 'edit' ? (
            <TouchableOpacity onPress={() => { /* clicking title could switch to inline edit - keep simple input fallback */ }}>
              <Text style={[styles.title, editableInline ? styles.titleInline : null]}>{title || (editableInline ? placeholderMap['餐厅'] : '未命名')}</Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.title}>{title || '未命名'}</Text>
          )}

          <View style={[styles.addrRow, editableInline ? styles.addrRowInline : null]}>
            <PlatformIcon name="location_on" size={16} color="#9CA3AF" />
            <TouchableOpacity style={{ flex: 1 }} onPress={() => { /* trigger inline edit on address - handled below by InlineRow */ }}>
              <Text style={[styles.addrText, editableInline ? styles.inlinePlaceholder : null]}>{address || (editableInline ? '添加地址' : '—')}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.infoListContainer}>
            {isLodging ? null : (
              <InlineEditRow
                label="建议时长"
                value={base.duration_minutes ? `${base.duration_minutes} 分钟` : ''}
                mode={mode}
                editableInline={editableInline}
                placeholder="例如 1 小時 30 分鐘"
                normalize={(raw) => {
                  const mins = parseDurationToMinutes(raw);
                  return typeof mins === 'number' && Number.isFinite(mins) ? String(mins) : undefined;
                }}
                onPatch={(v) => updateMetaPairsField('duration_minutes', v ? Number(v) : undefined)}
              />
            )}
            <InlineEditRow
              label="参考价位"
              value={base.price_range || ''}
              mode={mode}
              editableInline={editableInline}
              placeholder="待查询"
              normalize={(raw) => normalizePrice(raw)}
              onPatch={(v) => updateMetaPairsField('price_range', v)}
            />
            <InlineEditRow
              label="电话"
              value={base.phone || ''}
              mode={mode}
              editableInline={editableInline}
              placeholder="添加电话"
              onPatch={(v) => updateMetaPairsField('phone', v)}
            />
            <InlineEditRow
              label="坐标"
              value={base.coordinates || ''}
              mode={mode}
              editableInline={editableInline}
              placeholder="例如 22.28, 114.15"
              renderWhenView={(val) => {
                if (!val || typeof val !== 'string' || !val.includes(',')) return <Text style={styles.rowValue}>{val}</Text>;
                return (
                  <TouchableOpacity
                    onPress={() => {
                      try {
                        const url = `https://www.google.com/maps/search/?api=1&query=${val.trim()}`;
                        if (typeof window !== 'undefined') (window as any).open(url, '_blank');
                        else if (Linking && (Linking as any).openURL) (Linking as any).openURL(url);
                      } catch (e) {}
                    }}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.rowValue, { color: '#0ea5ff', textDecorationLine: 'underline' }]}>{val}</Text>
                  </TouchableOpacity>
                );
              }}
              onPatch={(v) => updateMetaPairsField('coordinates', v)}
            />
            <InlineEditRow
              label="网站"
              value={base.website || ''}
              mode={mode}
              editableInline={editableInline}
              placeholder="待查询网站"
              normalize={(raw) => normalizeWebsite(raw) || undefined}
              renderWhenView={(val) => {
                if (!val || !/^https?:\/\//i.test(val)) return <Text style={styles.rowValue}>{val}</Text>;
                return (
                  <TouchableOpacity
                    onPress={() => {
                      try {
                        if (typeof window !== 'undefined') (window as any).open(val, '_blank');
                        else if (Linking && (Linking as any).openURL) (Linking as any).openURL(val);
                      } catch (e) {}
                    }}
                    activeOpacity={0.8}
                  >
                    {(() => {
                      const { Icon, color } = pickSocialIcon(val);
                      return <Icon style={{ color, fontSize: 22 }} />;
                    })()}
                  </TouchableOpacity>
                );
              }}
              onPatch={(v) => updateMetaPairsField('website', v)}
            />
            <InlineEditRow
              label={isLodging ? '酒店' : '餐厅'}
              value={(isLodging ? (base.hotelName || base.restaurantName) : base.restaurantName) || base.title || ''}
              mode={mode}
              editableInline={editableInline}
              placeholder={isLodging ? '酒店名称' : '未命名'}
              onPatch={(v) => {
                // Title is a top-level field, not in metaPairs
                if (onChange) onChange({ title: v });
              }}
            />
            {isLodging ? null : (
              <>
                <InlineEditRow
                  label="菜系"
                  value={(Array.isArray(base.cuisine) ? base.cuisine.join(', ') : base.cuisine) || ''}
                  mode={mode}
                  editableInline={editableInline}
                  placeholder="例如 日本料理"
                  onPatch={(v) => updateMetaPairsField('cuisine', v)}
                />
                <InlineEditRow
                  label="推荐菜"
                  value={(Array.isArray(base.signatureDishes) ? base.signatureDishes.join(', ') : base.signatureDishes) || ''}
                  mode={mode}
                  editableInline={editableInline}
                  placeholder="待查询"
                  onPatch={(v) => updateMetaPairsField('signatureDishes', v)}
                />
              </>
            )}
            {isLodging ? (
              <>
                <InlineEditRow
                  label="星级"
                  value={(base.starRating || base.star || '') ? String(base.starRating || base.star) : ''}
                  mode={mode}
                  editableInline={editableInline}
                  placeholder="例如 5"
                  renderWhenView={(val) => (
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      {Array.from({ length: Math.max(0, Math.min(5, Number(val))) }).map((_, idx) => (
                        <FaStar key={idx} style={{ color: '#FFD700', fontSize: 18, marginLeft: idx > 0 ? 2 : 0 }} />
                      ))}
                    </View>
                  )}
                  onPatch={(v) => updateMetaPairsField('starRating', v ? Math.max(1, Math.min(5, Number(v))) : undefined)}
                />
                <InlineEditRow
                  label="设施"
                  value={(Array.isArray(base.amenities) ? base.amenities.join(', ') : base.amenities) || ''}
                  mode={mode}
                  editableInline={editableInline}
                  placeholder="例如 游泳池, 健身房"
                  onPatch={(v) => updateMetaPairsField('amenities', v)}
                />
                <InlineEditRow
                  label="入住时间"
                  value={base.checkInTime || ''}
                  mode={mode}
                  editableInline={editableInline}
                  placeholder="例如 15:00"
                  onPatch={(v) => updateMetaPairsField('checkInTime', v || undefined)}
                />
                <InlineEditRow
                  label="退房时间"
                  value={base.checkOutTime || ''}
                  mode={mode}
                  editableInline={editableInline}
                  placeholder="例如 11:00"
                  onPatch={(v) => updateMetaPairsField('checkOutTime', v || undefined)}
                />
              </>
            ) : null}
            <BookingPlatformsRow
              mode={mode}
              supabaseClient={supabaseClient}
              cardId={cardId}
              displayVariant={isLodging ? 'lodging' : 'restaurant'}
              value={base.bookingTips || ''}
              onChange={(v) => updateMetaPairsField('bookingTips', v)}
              platformProductId={resolvedPlatformProductId}
              platformProductIds={resolvedPlatformProductIds}
            />
          </View>
        </View>


      </View>
    </ScrollView>
  );
};

const InfoRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value || '—'}</Text>
  </View>
);

function formatLocation(loc: any) {
  if (!loc) return '';
  const parts: string[] = [];
  if (loc.placeName) parts.push(loc.placeName);
  if (loc.address) parts.push(loc.address);
  if (loc.city) parts.push(loc.city);
  if (loc.province) parts.push(loc.province);
  return parts.join(', ');
}

const styles = StyleSheet.create({
  container: { backgroundColor: '#fff', paddingBottom: 48 },
  coverWrap: { height: 220, backgroundColor: '#F3F4F6', overflow: 'hidden', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  coverImage: { width: '100%', height: '100%' },
  coverPlaceholder: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  coverActions: { position: 'absolute', right: 12, bottom: 12, flexDirection: 'row', gap: 8, zIndex: 2 },
  smallBtn: { backgroundColor: '#0ea5ff', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 },
  smallBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  contentRow: { flexDirection: 'row', padding: 16 },
  leftCol: { flex: 1, paddingRight: 0 },
  rightCol: { flex: 1, paddingLeft: 12 },
  title: { fontSize: 26, fontWeight: '800', color: '#111827' },
  titleInput: { fontSize: 22, fontWeight: '800', paddingVertical: 6, borderBottomWidth: 1, borderColor: '#F3F4F6' },
  labelTitle: { fontSize: 12, color: '#6B7280', marginBottom: 6 },
  addrRow: { flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 12 },
  addrText: { marginLeft: 8, color: '#8E8E93' },
  addrRowInline: { borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingBottom: 8, marginBottom: 12 },
  inlinePlaceholder: { color: '#A3AAB3' },
  inlineInput: { borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingVertical: 6, color: '#111' },
  titleInline: { borderBottomWidth: 3, borderBottomColor: '#111827', paddingBottom: 8, alignSelf: 'stretch' },
  addrInput: { marginLeft: 8, color: '#8E8E93', borderBottomWidth: 1, borderColor: '#F3F4F6', paddingVertical: 4 },
  infoList: { marginTop: 8 },
  infoListContainer: { marginTop: 8 },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderColor: '#F3F4F6' },
  rowLabelWrap: { width: 180 },
  rowLabel: { color: '#6B7280' },
  rowValueWrap: { flex: 1, alignItems: 'flex-end' },
  rowValue: { fontWeight: '700' },
  sectionHeader: { fontSize: 16, fontWeight: '700', marginBottom: 12 },
  pillList: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pill: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB', paddingHorizontal: 12, paddingVertical: 10, borderRadius: 8, marginRight: 8, marginBottom: 8 },
  pillText: { fontSize: 13 },
  formActions: { marginTop: 18, flexDirection: 'row', justifyContent: 'flex-end' },
  cancelBtn: { padding: 10, borderRadius: 8, backgroundColor: '#F3F4F6', marginRight: 8 },
  saveBtn: { padding: 10, borderRadius: 8, backgroundColor: '#0ea5ff' },
  bookingButtonGroup: { flexDirection: 'row', flexWrap: 'nowrap', gap: 10, justifyContent: 'flex-end' },
  bookingLogoBtn: { backgroundColor: 'transparent', paddingHorizontal: 0, paddingVertical: 0 },
  bookingLogo: { width: 28, height: 28, borderRadius: 4 },
});

export default RestaurantEditorView;
